// VEPC Gestao - Central Administrativa: Cliente de API e Estado Local
const API_BASE = (typeof window !== 'undefined' && window.__VEPC_API_BASE__) 
  || (import.meta.env?.VITE_API_BASE) 
  || '/api';

export const isAmbienteProducao = () => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host !== 'localhost' && host !== '127.0.0.1' && !host.endsWith('.local');
};

export const OPERADORES_DEMO = [
  { id: 'op-demo-super', nome: 'Administrador Demo', papel: 'superadmin_vepc', rotulo: 'Superadmin VEPC' },
  { id: 'op-demo-fin',   nome: 'Financeiro Demo',    papel: 'financeiro_vepc', rotulo: 'Financeiro VEPC' },
  { id: 'op-demo-sup',   nome: 'Suporte Demo',       papel: 'suporte_vepc',    rotulo: 'Suporte VEPC' },
  { id: 'op-demo-leit',  nome: 'Consulta Demo',      papel: 'leitura_vepc',    rotulo: 'Apenas Leitura' }
];

export const state = {
  operadorAtivo: OPERADORES_DEMO[0],
  ambiente: isAmbienteProducao() ? 'producao' : 'local',
  dadosDemo: !isAmbienteProducao(),
  viewAtual: 'dashboard',
  filtroEmpresas: '',
  modalAberto: null
};

export function setOperador(operadorId) {
  const op = OPERADORES_DEMO.find(o => o.id === operadorId) || OPERADORES_DEMO[0];
  state.operadorAtivo = op;
  localStorage.setItem('vepc_operador_id', op.id);
  window.dispatchEvent(new CustomEvent('vepc:operador_alterado', { detail: op }));
}

export function initOperador() {
  const salvo = localStorage.getItem('vepc_operador_id');
  if (salvo) {
    const op = OPERADORES_DEMO.find(o => o.id === salvo);
    if (op) state.operadorAtivo = op;
  }
}

async function request(endpoint, options = {}, isRetry = false) {
  let authHeader = options.headers?.Authorization;
  if (!authHeader) {
    const firebaseToken = typeof localStorage !== 'undefined' ? localStorage.getItem('vepc_admin_firebase_token') : null;
    if (firebaseToken) {
      authHeader = `Bearer ${firebaseToken}`;
    } else {
      if (isAmbienteProducao()) {
        throw new Error('Sessão administrativa expirada ou não autenticada. Faça login com o Firebase.');
      }
      authHeader = `Bearer demo.${state.operadorAtivo.id}`;
    }
  }

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': authHeader,
    ...(options.headers || {})
  };

  try {
    const res = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers
    });

    if (res.status === 401 && !isRetry && typeof window !== 'undefined') {
      const { obterTokenAdminAtual } = await import('./firebase.js');
      const novoToken = await obterTokenAdminAtual(true);
      if (novoToken) {
        return request(endpoint, {
          ...options,
          headers: { ...(options.headers || {}), Authorization: `Bearer ${novoToken}` }
        }, true);
      } else if (isAmbienteProducao()) {
        localStorage.removeItem('vepc_admin_firebase_token');
        window.dispatchEvent(new CustomEvent('vepc:sessao_expirada'));
      }
    }

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.erro || `Erro HTTP ${res.status}`);
    }
    return data;
  } catch (err) {
    console.warn(`[VEPC Admin API] Requisicao a ${endpoint} falhou:`, err.message);
    throw err;
  }
}

// Funções da API
export const api = {
  getMe: () => request('/admin/auth/me'),
  getDashboard: () => request('/admin/dashboard/metricas'),
  
  // Empresas
  getEmpresas: (params = '') => request(`/admin/empresas${params}`),
  getEmpresa: (id) => request(`/admin/empresas/${id}`),
  criarEmpresa: (dados) => request('/admin/empresas', { method: 'POST', body: JSON.stringify(dados) }),
  atualizarEmpresa: (id, dados) => request(`/admin/empresas/${id}`, { method: 'PUT', body: JSON.stringify(dados) }),
  alterarContrato: (id, dados) => request(`/admin/empresas/${id}/alterar-contrato`, { method: 'POST', body: JSON.stringify(dados) }),
  enviarConvite: (id) => request(`/admin/empresas/${id}/enviar-convite`, { method: 'POST' }),
  reenviarConvite: (id) => request(`/admin/empresas/${id}/reenviar-convite`, { method: 'POST' }),
  confirmarPrimeiroAcesso: (id) => request(`/admin/empresas/${id}/confirmar-primeiro-acesso`, { method: 'POST' }),
  adicionarUsuarioEmpresa: (id, dados) => request(`/admin/empresas/${id}/usuarios`, { method: 'POST', body: JSON.stringify(dados) }),

  // Planos
  getPlanos: () => request('/admin/planos'),
  atualizarPlano: (id, dados) => request(`/admin/planos/${id}`, { method: 'PUT', body: JSON.stringify(dados) }),

  // Mensalidades e Financeiro Inteligente
  getMensalidades: (params = '') => request(`/admin/mensalidades${params}`),
  getMensalidade: (id) => request(`/admin/mensalidades/${id}`),
  lancarMensalidade: (dados) => request('/admin/mensalidades', { method: 'POST', body: JSON.stringify(dados) }),
  simularJaPaguei: (id, dados) => request(`/admin/mensalidades/${id}/ja-paguei`, { method: 'POST', body: JSON.stringify(dados) }),
  conferirMensalidade: (id, dados) => request(`/admin/mensalidades/${id}/conferir`, { method: 'POST', body: JSON.stringify(dados) }),
  regularizarMensalidade: (id, dados) => request(`/admin/mensalidades/${id}/regularizar`, { method: 'POST', body: JSON.stringify(dados) }),
  
  // Financeiro Inteligente & Custos
  getFinanceiroAnalise: (params = '') => request(`/admin/financeiro/analise${params}`),
  registrarCusto: (dados) => request('/admin/financeiro/custos', { method: 'POST', body: JSON.stringify(dados) }),

  // Alertas do Sistema
  getAlertas: () => request('/admin/alertas'),
  marcarAlertaLido: (id) => request(`/admin/alertas/${id}/lido`, { method: 'POST' }),

  // Infraestrutura & Diagnostico
  getInfraStatus: () => request('/admin/infra/status'),
  getVersoesApp: () => request('/admin/infra/versoes'),
  getHistoricoTecnico: () => request('/admin/infra/historico'),
  executarDiagnostico: () => request('/admin/infra/diagnostico/executar', { method: 'POST' }),

  // Suporte & Erros
  getChamados: (params = '') => request(`/admin/suporte/chamados${params}`),
  getChamado: (id) => request(`/admin/suporte/chamados/${id}`),
  responderChamado: (id, dados) => request(`/admin/suporte/chamados/${id}/mensagens`, { method: 'POST', body: JSON.stringify(dados) }),
  getErros: () => request('/admin/suporte/erros'),
  resolverErro: (id) => request(`/admin/suporte/erros/${id}/resolver`, { method: 'POST' }),

  // Auditoria
  getAuditoria: (params = '') => request(`/admin/auditoria${params}`),

  // Onboarding do Cliente (Primeiro Acesso Seguro)
  validarConvite: (token) => fetch(`${API_BASE}/onboarding/validar?token=${encodeURIComponent(token)}`).then(async res => {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.erro || `Erro HTTP ${res.status}`);
      err.codigo = data.codigo;
      throw err;
    }
    return data;
  }),
  ativarOnboarding: (token, firebaseIdToken) => fetch(`${API_BASE}/onboarding/ativar`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${firebaseIdToken}`
    },
    body: JSON.stringify({ token_convite: token })
  }).then(async res => {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.erro || `Erro HTTP ${res.status}`);
      err.codigo = data.codigo;
      throw err;
    }
    return data;
  })
};
